import React from 'react';

import { Currency } from '../../types/apiEnums';
import {
  EventTypes,
  DataLayerViewItem,
  DataLayerProductItemV2,
  DataLayerAddToCartItem,
  DataLayerPurchasePaymentType,
  DataLayerViewItemList,
  DataLayerSelectItem,
} from '../../types/dataLayer';

export type SelectedItemParams = {
  itemVariant: number | string;
};

export type AddShippingInfoParams = {
  items: DataLayerProductItemV2[];
  name: string;
  postalCode?: string;
  variationCurrency: Currency;
  variationPrice: number;
};

export type ViewCartParams = {
  items: DataLayerProductItemV2[];
  shipping: number;
  totalExclusivePriceCurrency: Currency;
  totalExclusivePriceValue: number;
};

export type PurchaseEventParams = {
  client_type: string;
  companyName: string;
  currency: Currency;
  email: string;
  items: DataLayerProductItemV2[];
  lastName: string;
  name: string;
  payment_type: DataLayerPurchasePaymentType;
  phoneNumber: string;
  shipping: number;
  transaction_id: string;
  value: number;
};

export type PageViewEventParams = {
  page_location: string;
  page_path: string;
};

export type FormSubmitQuotationEventParams = {
  email: string;
  formLocation: FormLocationDataLayer;
  phoneNumber: string;
};

export enum FormLocationDataLayer {
  businessPDPModal = 'business_pdp_modal',
  businessPage = 'business_page',
  businessSubpage = 'business_subpage',
  pdpBusinessButton = 'pdp_business_button',
}

export const dataLayerV2Hooks = {
  useTrackSelectItemVariant(event: EventTypes) {
    return React.useCallback(({
      itemVariant,
    }: SelectedItemParams) => {
      window.dataLayer.push({
        event,
        selected_item_variant: itemVariant,
      });
    }, [event]);
  },

  useTrackViewItem() {
    return React.useCallback((product: DataLayerViewItem) => {
      window.dataLayer.push({
        event: EventTypes.viewItem,
        ecommerce: {
          items: [product],
        },
      });
    }, []);
  },

  useTrackAddShippingInfo() {
    return React.useCallback(({
      name,
      postalCode,
      variationCurrency,
      variationPrice,
      items,
    }: AddShippingInfoParams) => {
      window.dataLayer.push({
        event: EventTypes.addShippingInfo,
        enhanced_data: {
          name,
          postal_code: postalCode,
        },
        ecommerce: {
          currency: variationCurrency,
          value: variationPrice,
          items,
        },
      });
    }, []);
  },

  useTrackAddToCart(products: DataLayerAddToCartItem[] | null) {
    return React.useCallback(() => {
      if (products) {
        window.dataLayer.push({
          event: EventTypes.addToCartV2,
          ecommerce: {
            items: products,
          },
        });
      }
    }, [products]);
  },

  useTrackCartQuantityOnBasket() {
    return React.useCallback((
      products: DataLayerAddToCartItem[] | null,
      event: EventTypes.addToCartV2 | EventTypes.removeFromCartV2,
    ) => {
      if (products) {
        window.dataLayer.push({
          event,
          ecommerce: {
            items: products.map(el => ({
              ...el,
              quantity: 1,
            })),
          },
        });
      }
    }, []);
  },

  useTrackCartQuantityOnBasketV2() {
    return React.useCallback((
      products: DataLayerProductItemV2[] | undefined,
      event: EventTypes.addToCartV2 | EventTypes.removeFromCartV2,
    ) => {
      if (products) {
        window.dataLayer.push({
          event,
          ecommerce: {
            items: products.map(el => ({
              ...el,
              quantity: 1,
            })),
          },
        });
      }
    }, []);
  },

  useTrackRemoveFromCart() {
    return React.useCallback((products: DataLayerAddToCartItem[] | null) => {
      if (products) {
        window.dataLayer.push({
          event: EventTypes.removeFromCartV2,
          ecommerce: {
            items: products,
          },
        });
      }
    }, []);
  },

  useTrackViewCart() {
    return React.useCallback((
      {
        totalExclusivePriceCurrency,
        totalExclusivePriceValue,
        items,
        shipping,
      }: ViewCartParams,
    ) => {
      window.dataLayer.push({
        event: EventTypes.viewCart,
        ecommerce: {
          currency: totalExclusivePriceCurrency,
          value: totalExclusivePriceValue,
          items,
          shipping,
        },
      });
    }, []);
  },

  useTrackBeginCheckout() {
    return React.useCallback((items: DataLayerProductItemV2[]) => {
      window.dataLayer.push({
        event: EventTypes.beginCheckout,
        ecommerce: { items },
      });
    }, []);
  },

  useTrackPurchase() {
    return React.useCallback(({
      payment_type,
      companyName,
      email,
      phoneNumber,
      name,
      lastName,
      transaction_id,
      value,
      shipping,
      currency,
      items,
      client_type,
    }: PurchaseEventParams) => {
      window.dataLayer.push({
        event: EventTypes.purchase,
        payment_type,
        companyName,
        email,
        phoneNumber,
        name,
        lastName,
        client_type,
        ecommerce: {
          transaction_id,
          value,
          shipping,
          currency,
          items,
        },
      });
    }, []);
  },

  useTrackPaymentInfo(params: {
    currency: Currency;
    items: DataLayerProductItemV2[];
    payment_type: string;
    value: number;
  } | null) {
    return React.useCallback(() => {
      if (params) {
        const { items, payment_type, currency, value } = params;
        window.dataLayer.push({
          event: EventTypes.addPaymentInfo,
          ecommerce: {
            items,
            payment_type,
            currency,
            value,
          },
        });
      }
    }, [params]);
  },
  useTrackPageView() {
    return React.useCallback(({
      page_location,
      page_path,
    }: PageViewEventParams) => {
      window.dataLayer.push({
        event: EventTypes.pageView,
        page: {
          page_location,
          page_path,
        },
      });
    }, []);
  },
  useTrackViewItemListDispatch() {
    return React.useCallback((item: DataLayerViewItemList) => {
      const event = new CustomEvent('addItemToDataLayer', { detail: item, bubbles: true });
      window.dispatchEvent(event);
    }, []);
  },
  useTrackViewItemList() {
    return React.useCallback((items: DataLayerViewItemList[]) => {
      window.dataLayer.push({
        event: EventTypes.viewItemList,
        ecommerce: {
          items,
        },
      });
    }, []);
  },
  useTrackViewItemListHandler() {
    const itemsRef = React.useRef<DataLayerViewItemList[]>([]);
    const trackViewItemsList = dataLayerV2Hooks.useTrackViewItemList();
    const timeoutIdRef = React.useRef<NodeJS.Timeout | null>(null);

    const itemHandler = React.useCallback((event: CustomEvent<DataLayerViewItemList>) => {
      itemsRef.current = [...itemsRef.current, event.detail];

      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
      }

      timeoutIdRef.current = setTimeout(() => {
        if (itemsRef.current.length > 0) {
          trackViewItemsList(itemsRef.current);
          itemsRef.current = [];
        }
      }, 1000);
    }, [trackViewItemsList]);

    React.useEffect(() => {
      window.addEventListener('addItemToDataLayer', itemHandler);

      return () => {
        window.removeEventListener('addItemToDataLayer', itemHandler);
        if (timeoutIdRef.current !== null) {
          clearTimeout(timeoutIdRef.current);
        }
      };
    }, [itemHandler, trackViewItemsList]);

    return { timeoutIdRef, itemsRef, itemHandler };
  },
  useTrackSelectItem() {
    return React.useCallback(({
      item_id,
      item_name,
      item_category,
      item_list_name,
      item_list_id,
      index,
    }: DataLayerSelectItem) => {
      window.dataLayer.push({
        event: EventTypes.selectItem,
        ecommerce: {
          items: [{
            item_id,
            item_name,
            item_category,
            item_list_name,
            item_list_id,
            index,
            quantity: 1,
          }],
        },
      });
    }, []);
  },
  useTrackFormSubmitQuotation() {
    return React.useCallback(({
      email,
      phoneNumber,
      formLocation,
    }: FormSubmitQuotationEventParams) => {
      window.dataLayer.push({
        event: EventTypes.formSubmitQuotation,
        email,
        phoneNumber,
        formLocation,
      });
    }, []);
  },
};
